import { MouseEvent, useCallback } from 'react';
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ActivityComment,
  ActivityKind,
  ActivityStateAction,
  AnyActivity,
  AnyActivityDetail,
  Event,
  GlobalActivityStateAction,
  UserActivityStateAction
} from '@blisspointmedia/bpm-types/dist/OracleActivity';
import {
  CreateOracleEventBody,
  CreateOracleEventResponse,
  DeleteActivityCommentResponse,
  EditOracleEventBody,
  EditOracleEventResponse,
  GetOracleActivitiesResponse,
  ModifyActivityStateBody,
  ModifyActivityStateResponse,
  OracleEventEditResult,
  PatchActivityCommentBody,
  PatchActivityCommentResponse,
  PostActivityCommentBody,
  PostActivityCommentResponse
} from '@blisspointmedia/bpm-types/dist/OracleApi';
import { useSetError } from '@src/redux/modals';
import { OracleLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import { ActivityId } from './activity-feed-data-utils';
import { EVENTS_QUERY_KEY } from './activity-feed-constants';
import { useActivityFeedData } from './useActivityFeedData';
import { oracleActivityQueryKey } from './useOracleActivity';
import { memberIds } from './useOracleEventsData';

const modifyEventState = async (
  body: ModifyActivityStateBody
): Promise<ModifyActivityStateResponse> => {
  const res = await OracleLambdaFetch<ModifyActivityStateBody>('/events/state', {
    method: 'PATCH',
    body
  });
  return awaitJSON<ModifyActivityStateResponse>(res);
};

interface PostCommentArgs {
  activityId: ActivityId;
  body: PostActivityCommentBody;
}

const postEventComment = async ({
  activityId,
  body
}: PostCommentArgs): Promise<PostActivityCommentResponse> => {
  const res = await OracleLambdaFetch<PostActivityCommentBody>(
    `/events/${encodeURIComponent(activityId)}/comments`,
    {
      method: 'POST',
      body
    }
  );
  return awaitJSON<PostActivityCommentResponse>(res);
};

// A folded card shows comments from every member event, and the lambda only finds a comment
// under the event it was written on, so the URL uses the comment's own event, not the card's.
export const commentPath = (comment: ActivityComment): string =>
  `/events/${encodeURIComponent(comment.activityId)}/comments/${encodeURIComponent(comment.id)}`;

interface PatchCommentArgs {
  activityId: ActivityId;
  comment: ActivityComment;
  body: PatchActivityCommentBody;
}

const patchEventCommentFetch = async ({
  comment,
  body
}: PatchCommentArgs): Promise<PatchActivityCommentResponse> => {
  const res = await OracleLambdaFetch<PatchActivityCommentBody>(commentPath(comment), {
    method: 'PATCH',
    body
  });
  return awaitJSON<PatchActivityCommentResponse>(res);
};

interface DeleteCommentArgs {
  activityId: ActivityId;
  comment: ActivityComment;
}

const deleteEventCommentFetch = async ({
  comment
}: DeleteCommentArgs): Promise<DeleteActivityCommentResponse> => {
  const res = await OracleLambdaFetch(commentPath(comment), { method: 'DELETE' });
  return awaitJSON<DeleteActivityCommentResponse>(res);
};

const editEventFetch = async (body: EditOracleEventBody): Promise<EditOracleEventResponse> => {
  const res = await OracleLambdaFetch<EditOracleEventBody>('/events/overrides', {
    method: 'POST',
    body
  });
  return awaitJSON<EditOracleEventResponse>(res);
};

const createEvent = async (body: CreateOracleEventBody): Promise<CreateOracleEventResponse> => {
  const res = await OracleLambdaFetch<CreateOracleEventBody>('/events', {
    method: 'POST',
    body
  });
  return awaitJSON<CreateOracleEventResponse>(res);
};

// Apply the flag flip (or removal, for DISMISS) that the given action should produce on the
// target activity. Returns a new registry — never mutates. If the entry isn't present, returns
// the prev registry unchanged (nothing sensible to patch). UNDISMISS is a no-op client-side
// since dismissed activities aren't in the registry to begin with. Exported for unit test.
export const applyOptimisticPatch = (
  prev: Record<ActivityId, AnyActivity>,
  activityId: ActivityId,
  action: ActivityStateAction
): Record<ActivityId, AnyActivity> => {
  const existing = prev[activityId];
  if (!existing) return prev;
  // Narrow the patch shape to the mutable flag fields — Partial<AnyActivity> would fail the
  // discriminated-union narrowing on `kind` when spread onto the existing entry.
  type FlagPatch = Partial<Pick<AnyActivity, 'isStarred' | 'isRead' | 'isHidden' | 'isPinned'>>;
  const patch = (updates: FlagPatch) => ({
    ...prev,
    [activityId]: { ...existing, ...updates }
  });
  switch (action) {
    case UserActivityStateAction.STAR:
      return patch({ isStarred: true });
    case UserActivityStateAction.UNSTAR:
      return patch({ isStarred: false });
    case UserActivityStateAction.MARK_READ:
      return patch({ isRead: true });
    case UserActivityStateAction.MARK_UNREAD:
      return patch({ isRead: false });
    case UserActivityStateAction.HIDE:
      return patch({ isHidden: true });
    case UserActivityStateAction.UNHIDE:
      return patch({ isHidden: false });
    case GlobalActivityStateAction.PIN:
      return patch({ isPinned: true });
    case GlobalActivityStateAction.UNPIN:
      return patch({ isPinned: false });
    case GlobalActivityStateAction.DISMISS: {
      const next = { ...prev };
      delete next[activityId];
      return next;
    }
    case GlobalActivityStateAction.UNDISMISS:
      return prev;
    default:
      return prev;
  }
};

// Walk every page of the infinite-query cache and apply `update` to the matching activity.
// Activities not matching `activityId` are returned unchanged. Exported for unit test.
export const mapEventInPages = (
  old: InfiniteData<GetOracleActivitiesResponse>,
  activityId: ActivityId,
  update: (activity: AnyActivity) => AnyActivity
): InfiniteData<GetOracleActivitiesResponse> => ({
  ...old,
  pages: old.pages.map(page => ({
    ...page,
    events: page.events.map(activity => (activity.id === activityId ? update(activity) : activity))
  }))
});

// Reconcile the React Query cache with the server-confirmed activity. DISMISS drops the
// entry; every other action replaces it with the server's copy so the cache = source of
// truth once the server has spoken. Next refetch is a no-op. Cache is InfiniteData because
// the loader is a useInfiniteQuery — walk every page and patch whichever one contains the
// activity. Exported for unit test.
export const reconcileQueryCache = (
  old: InfiniteData<GetOracleActivitiesResponse> | undefined,
  activityId: ActivityId,
  action: ActivityStateAction,
  serverActivity: AnyActivity
): InfiniteData<GetOracleActivitiesResponse> | undefined => {
  if (!old) return old;
  if (action === GlobalActivityStateAction.DISMISS) {
    return {
      ...old,
      pages: old.pages.map(page => ({
        ...page,
        events: page.events.filter(e => e.id !== activityId)
      }))
    };
  }
  return mapEventInPages(old, activityId, () => serverActivity);
};

// Optimistic state-modify mutation. Patches `activitiesById` in the ActivityFeed context on
// mutate; reverts to the pre-mutation snapshot on error; reconciles the query cache on success.
// Not exported — UI call sites use {@link useToggleActivityState}, which composes this one.
// If a future caller needs the raw mutation, re-export.
const useModifyEventState = () => {
  const queryClient = useQueryClient();
  const { activitiesById, setActivitiesById } = useActivityFeedData();
  const setError = useSetError();
  return useMutation({
    mutationFn: modifyEventState,
    onMutate: ({ activityId, action }) => {
      // Read the snapshot from the current context value rather than inside the setState
      // updater. React only runs the updater synchronously on the eager-bailout fast path;
      // when the provider fiber has pending update lanes (e.g. two rapid toggles or a click
      // mid-resync) the updater defers to the next render and the assigned-in-updater
      // snapshot is still undefined when onMutate returns.
      const snapshot = activitiesById[activityId];
      setActivitiesById(prev => applyOptimisticPatch(prev, activityId, action));
      return { snapshot };
    },
    onError: (err, { activityId }, ctx) => {
      if (ctx?.snapshot) {
        const restored = ctx.snapshot;
        setActivitiesById(prev => ({ ...prev, [activityId]: restored }));
      }
      setError({ message: `Couldn't update event: ${err.message}`, reportError: err });
    },
    onSuccess: (data, { activityId, action }) => {
      queryClient.setQueriesData<InfiniteData<GetOracleActivitiesResponse>>(
        { queryKey: EVENTS_QUERY_KEY },
        old => reconcileQueryCache(old, activityId, action, data.activity)
      );
    }
  });
};

// Append `comment` to the details cache for `activityId` and bump commentCount on both the
// envelope and the resolved shadow. If the cache entry isn't populated yet (user posted before
// the details query resolved), leave it alone — the next fetch will bring the server truth.
export const appendCommentToDetail = <T extends AnyActivityDetail>(
  old: T | undefined,
  comment: ActivityComment
): T | undefined => {
  if (!old) return old;
  const existing = old.comments ?? [];
  // AnyActivityDetail is a union of *Details shapes, each with its own `resolved: X`. Spreading
  // the union widens the discriminant on `resolved`; cast preserves the input variant's type.
  const next = {
    ...old,
    commentCount: old.commentCount + 1,
    comments: [...existing, comment],
    resolved: { ...old.resolved, commentCount: old.resolved.commentCount + 1 }
  };
  return next;
};

// Bump commentCount on whichever page of the infinite-query cache holds this activity. Missing
// activity (e.g. user posted on a details view opened via deep link) → cache untouched.
export const bumpListCommentCount = (
  old: InfiniteData<GetOracleActivitiesResponse> | undefined,
  activityId: ActivityId
): InfiniteData<GetOracleActivitiesResponse> | undefined => {
  if (!old) return old;
  return mapEventInPages(old, activityId, activity => ({
    ...activity,
    commentCount: activity.commentCount + 1
  }));
};

export const usePostEventComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postEventComment,
    onSuccess: ({ comment }, { activityId }) => {
      queryClient.setQueriesData<AnyActivityDetail>(
        { queryKey: oracleActivityQueryKey(activityId) },
        old => appendCommentToDetail(old, comment)
      );
      queryClient.setQueriesData<InfiniteData<GetOracleActivitiesResponse>>(
        { queryKey: EVENTS_QUERY_KEY },
        old => bumpListCommentCount(old, activityId)
      );
    }
  });
};

// Swap the target comment in the details cache with the server's copy (post-edit or
// post-tombstone). commentCount is untouched — edits don't move it, and deletes are handled
// separately by decrementListCommentCount below (list-envelope count excludes tombstones).
export const replaceCommentInDetail = (
  old: AnyActivityDetail | undefined,
  comment: ActivityComment
): AnyActivityDetail | undefined => {
  if (!old || !old.comments) return old;
  return {
    ...old,
    comments: old.comments.map(existing => (existing.id === comment.id ? comment : existing))
  };
};

// Mirror of bumpListCommentCount but decrements. Server-side count excludes tombstones, so a
// successful soft-delete must be reflected on the summary-card badge. No-op when the activity
// isn't cached or its count is already 0 (guard against desync). Matches the page item whose
// group holds the comment's event, since a merged card's counts sit on more than one item.
export const decrementListCommentCount = (
  old: InfiniteData<GetOracleActivitiesResponse> | undefined,
  commentEventId: ActivityId
): InfiniteData<GetOracleActivitiesResponse> | undefined => {
  if (!old) return old;
  return {
    ...old,
    pages: old.pages.map(page => ({
      ...page,
      events: page.events.map(activity =>
        memberIds(activity).includes(commentEventId) && activity.commentCount > 0
          ? { ...activity, commentCount: activity.commentCount - 1 }
          : activity
      )
    }))
  };
};

export const usePatchEventComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchEventCommentFetch,
    onSuccess: ({ comment }, { activityId }) => {
      queryClient.setQueriesData<AnyActivityDetail>(
        { queryKey: oracleActivityQueryKey(activityId) },
        old => replaceCommentInDetail(old, comment)
      );
    }
  });
};

export const useDeleteEventComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteEventCommentFetch,
    onSuccess: ({ comment }, { activityId }) => {
      queryClient.setQueriesData<AnyActivityDetail>(
        { queryKey: oracleActivityQueryKey(activityId) },
        old => replaceCommentInDetail(old, comment)
      );
      queryClient.setQueriesData<InfiniteData<GetOracleActivitiesResponse>>(
        { queryKey: EVENTS_QUERY_KEY },
        old => decrementListCommentCount(old, comment.activityId)
      );
    }
  });
};

// A cleared optional field arrives as null, and the list item leaves it unset.
export const applyEditResults = (
  old: InfiniteData<GetOracleActivitiesResponse> | undefined,
  eventIds: ActivityId[],
  results: OracleEventEditResult[]
): InfiniteData<GetOracleActivitiesResponse> | undefined => {
  if (!old) return old;
  const patch: Partial<Event> = Object.fromEntries(
    results
      .filter(result => result.success)
      .map(({ edit }) => [edit.field, edit.value ?? undefined])
  );
  if (Object.keys(patch).length === 0) return old;
  return {
    ...old,
    pages: old.pages.map(page => ({
      ...page,
      events: page.events.map(activity =>
        activity.kind === ActivityKind.EVENT && eventIds.includes(activity.id)
          ? { ...activity, ...patch }
          : activity
      )
    }))
  };
};

// The panel renders the list item, so saved fields are written into it before the form closes.
// The list still refetches, because an eventType edit re-derives signal and importance upstream.
export const useEditEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editEventFetch,
    onSuccess: ({ results }, { eventId, foldedIds }) => {
      queryClient.setQueriesData<InfiniteData<GetOracleActivitiesResponse>>(
        { queryKey: EVENTS_QUERY_KEY },
        old => applyEditResults(old, [eventId, ...(foldedIds ?? [])], results)
      );
      queryClient.invalidateQueries({ queryKey: EVENTS_QUERY_KEY });
      return queryClient.invalidateQueries({ queryKey: oracleActivityQueryKey(eventId) });
    }
  });
};

export const useCreateEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENTS_QUERY_KEY });
    }
  });
};

/**
 * Toggle helper for the star / pin / read / hide / dismiss surfaces. Returns a stable
 * `(e, cid, activityId, action)` handler — hoist this to a parent that isn't inside a
 * memoized card render, then pass the returned function down as a prop so every card
 * doesn't subscribe to ActivityFeed context (defeats memo(EventCard) for the non-mutated
 * cards during a state change).
 *
 * Callers pick the action themselves — pair with {@link nextStarAction} / {@link nextPinAction}
 * so the on/off decision lives in one place per flag. `e.stopPropagation()` fires here so
 * cards embedded in an outer clickable container don't also fire the parent onClick.
 */
export const useToggleActivityState = () => {
  const { mutateAsync: modifyState } = useModifyEventState();
  return useCallback(
    (e: MouseEvent, activityId: ActivityId, action: ActivityStateAction) => {
      e.stopPropagation();
      return modifyState({ activityId, action });
    },
    [modifyState]
  );
};
